import type { RouteObject } from "react-router-dom";
import NotFound from "../pages/NotFound";
import Home from "../pages/home/page";
import BelvederePage from "../pages/property-detail/page";
import HannahsPearlPage from "../pages/property-detail/hannahs-pearl-page";
import SeaDreamsPage from "../pages/property-detail/sea-dreams-page";
import BeachBeachBabyPage from "../pages/property-detail/beach-beach-baby-page";
import BellaVitaPage from "../pages/property-detail/bella-vita-page";
import SunsetBungalow30aPage from "../pages/property-detail/sunset-bungalow-30a-page";
import OwnersPage from "../pages/owners/page";
import CollectionPage from "../pages/collection/page";
import MapSearchPage from "../pages/map-search/page";
import AccessibilityStatementPage from "../pages/accessibility-statement/page";
import OwnerRezBelvedereProofPage from "../pages/property-detail/ownerrez-belvedere-proof-page";
import OwnerRezBelvedereCuratedPage from "../pages/property-detail/ownerrez-belvedere-curated-page";
import OwnerRezPricingProofPage from "../pages/ownerrez-pricing-proof/page";

const routes: RouteObject[] = [
  {
    path: "/",
    element: <Home />,
  },
  {
    path: "/the-belvedere",
    element: <BelvederePage />,
  },
  {
    path: "/ownerrez-belvedere-proof",
    element: <OwnerRezBelvedereProofPage />,
  },
  {
    path: "/belvedere-data-preview",
    element: <OwnerRezBelvedereProofPage />,
  },
  {
    path: "/ownerrez-belvedere-after",
    element: <OwnerRezBelvedereCuratedPage />,
  },
  {
    path: "/belvedere-curated-preview",
    element: <OwnerRezBelvedereCuratedPage />,
  },
  {
    path: "/ownerrez-pricing-proof",
    element: <OwnerRezPricingProofPage />,
  },
  {
    path: "/pricing-preview",
    element: <OwnerRezPricingProofPage />,
  },
  {
    path: "/hannahs-pearl",
    element: <HannahsPearlPage />,
  },
  {
    path: "/sea-dreams",
    element: <SeaDreamsPage />,
  },
  {
    path: "/beach-beach-baby",
    element: <BeachBeachBabyPage />,
  },
  {
    path: "/bella-vita",
    element: <BellaVitaPage />,
  },
  {
    path: "/sunset-bungalow-30a",
    element: <SunsetBungalow30aPage />,
  },
  {
    path: "/join-our-rental-program",
    element: <OwnersPage />,
  },
  {
    path: "/vacation-search-results",
    element: (
      <CollectionPage
        eyebrow="All Vacation Properties"
        title="Explore the Inspire collection."
        subtitle="Explore a thoughtful collection of Emerald Coast vacation properties, from Panama City Beach to 30A."
        filter={() => true}
        progressiveReveal
      />
    ),
  },
  {
    path: "/map-search",
    element: <MapSearchPage />,
  },
  {
    path: "/accessibility-statement",
    element: <AccessibilityStatementPage />,
  },
  {
    path: "/30a-area-search",
    element: (
      <CollectionPage
        eyebrow="30A Area"
        title="30A vacation properties."
        subtitle="A quieter Emerald Coast rhythm, from Rosemary Beach to WaterSound and Dune Allen."
        filter={(property) => property.communities.includes('30A')}
      />
    ),
  },
  {
    path: "/pcb-area-search",
    element: (
      <CollectionPage
        eyebrow="Panama City Beach"
        title="Panama City Beach vacation properties."
        subtitle="Gulf-front condos, beachfront stays, and roomy beach properties close to the energy of Panama City Beach."
        filter={(property) => property.communities.includes('Panama City Beach')}
      />
    ),
  },
  {
    path: "/gulf-front-search-results",
    element: (
      <CollectionPage
        eyebrow="Gulf Front"
        title="Gulf-front and beachfront stays."
        subtitle="Properties where the Gulf is the main event."
        filter={(property) => property.beachfront}
      />
    ),
  },
  {
    path: "/pool-search-results",
    element: (
      <CollectionPage
        eyebrow="Has A Pool"
        title="Properties with pool time built in."
        subtitle="Private pools, resort pools, splash pads, and sunny places to unwind between beach days."
        filter={(property) =>
          property.privatePool ||
          property.verifiedAmenityPhrases.some((phrase) => /\bpools?\b|\bsplash\s*pad\b|\bhot\s*tub\b/i.test(phrase))
        }
      />
    ),
  },
  {
    path: "/golf-cart-search-results",
    element: (
      <CollectionPage
        eyebrow="Golf Cart"
        title="Properties with a golf cart."
        subtitle="Properties where getting around feels easy, breezy, and part of the fun."
        filter={(property) => property.golfCart}
      />
    ),
  },
  {
    path: "/outdoor-living-search-results",
    element: (
      <CollectionPage
        eyebrow="Outdoor Living"
        title="Properties with outdoor living."
        subtitle="Porches, balconies, rooftops, grills, and gathering spaces made for coastal days outside."
        filter={(property) =>
          property.outdoorLiving || property.rooftop || property.summerKitchen || property.verifiedAmenityPhrases.some((phrase) => /outdoor|balcon|porch|rooftop|terrace|grill/i.test(phrase))
        }
      />
    ),
  },
  {
    path: "/large-groups-search-results",
    element: (
      <CollectionPage
        eyebrow="Large Groups"
        title="Properties for larger groups."
        subtitle="Roomier stays for families and groups who want space to gather and room to spread out."
        filter={(property) => property.bedrooms >= 4 && property.sleeps >= 12}
        sort={(a, b) => b.sleeps - a.sleeps || b.bedrooms - a.bedrooms}
      />
    ),
  },
  {
    path: "/pets-search-results",
    element: (
      <CollectionPage
        eyebrow="Pet Friendly"
        title="Pet-friendly vacation properties."
        subtitle="Vacation properties where pets are welcome, with property-specific approval and fees when required."
        filter={(property) => Boolean(property.petPolicy)}
      />
    ),
  },
  {
    path: "/rosemary-beach-search",
    element: (
      <CollectionPage
        eyebrow="Rosemary Beach"
        title="Rosemary Beach vacation properties."
        subtitle="In the heart of 30A, close to restaurants, boutiques, beach paths, and Rosemary Beach town life."
        filter={(property) => property.communities.includes('Rosemary Beach')}
      />
    ),
  },
  {
    path: "/dune-allen",
    element: (
      <CollectionPage
        eyebrow="Dune Allen"
        title="Dune Allen vacation properties."
        subtitle="A relaxed 30A setting with beach access, lake views, and favorite local dining nearby."
        filter={(property) => property.communities.includes('Dune Allen')}
      />
    ),
  },
  {
    path: "/santa-rosa-beach",
    element: (
      <CollectionPage
        eyebrow="Santa Rosa Beach"
        title="Santa Rosa Beach vacation properties."
        subtitle="A relaxed stretch of the 30A area with beach access, neighborhood charm, and room to settle in."
        filter={(property) => property.communities.includes('Santa Rosa Beach')}
      />
    ),
  },
  {
    path: "/watersound",
    element: (
      <CollectionPage
        eyebrow="WaterSound"
        title="WaterSound vacation properties."
        subtitle="A private, calmer 30A setting with beach access and space to settle in."
        filter={(property) => property.communities.includes('WaterSound')}
      />
    ),
  },
  {
    path: "/watercolor-area",
    element: (
      <CollectionPage
        eyebrow="WaterColor"
        title="WaterColor vacation properties."
        subtitle="A beloved 30A setting known for coastal charm, bike paths, beach days, and easygoing town-center moments."
        filter={(property) => property.communities.includes('Watercolor')}
      />
    ),
  },
  {
    path: "/blue-mountain-beach-search",
    element: (
      <CollectionPage
        eyebrow="Blue Mountain Beach"
        title="Blue Mountain Beach vacation properties."
        subtitle="A laid-back 30A favorite with a slower rhythm, local flavor, and sugar-white sand nearby."
        filter={() => false}
      />
    ),
  },
  {
    path: "/inlet-beach-search",
    element: (
      <CollectionPage
        eyebrow="Inlet Beach"
        title="Inlet Beach vacation properties."
        subtitle="A quieter east-end 30A setting with beach access, local dining, and easy coastal days."
        filter={() => false}
      />
    ),
  },
  {
    path: "/destin-area-search",
    element: (
      <CollectionPage
        eyebrow="Destin Area"
        title="Destin area vacation properties."
        subtitle="A classic Emerald Coast favorite known for bright Gulf water, beaches, boating, and family fun."
        filter={() => false}
      />
    ),
  },
  {
    path: "/miramar-beach-search",
    element: (
      <CollectionPage
        eyebrow="Miramar Beach"
        title="Miramar Beach vacation properties."
        subtitle="Beach days, shopping, dining, and Emerald Coast ease in one familiar stretch."
        filter={() => false}
      />
    ),
  },
  {
    path: "*",
    element: <NotFound />,
  },
];

export default routes;
